import React, { useEffect, useState } from 'react';
import { Check, Star, Sparkles } from 'lucide-react';
import { fetchMembershipPricing } from '../services/FirebaseService';

export default function PricingSection({ onSelectMembership, onStartBooking }) {
  // Precio mensual real desde el backend (Firestore dais_membership/<id>,
  // campo monthly_price). Las cards de membresía muestran el equivalente
  // mensual; la consulta individual no depende de esto.
  const [backendPricing, setBackendPricing] = useState({});
  useEffect(() => {
    let cancelled = false;
    fetchMembershipPricing([21, 25])
      .then((map) => { if (!cancelled) setBackendPricing(map || {}); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const plans = [
    {
      name: 'Membresía Integral',
      price: '399',
      membershipId: 21, // Odoo membership ID
      description: 'Perfecto para cuidado preventivo y bienestar general',
      features: [
        { text: '70 minutos de consultas en especialidades incluidas' },
        { text: 'Más minutos a precio especial' },
        { text: '5 preguntas de orientación médica' },
        { text: 'Recetas y planes personalizados' },
        { text: 'Acceso a todas las funciones de la App 24/7' },
        { text: 'Renovación automática, cancela cuando desees' }
      ],
      highlighted: false,
      cta: 'Comenzar ahora',
      isMembership: true
    },
    {
      name: 'Membresía Anual',
      price: '334',
      membershipId: 25, // Odoo membership ID
      description: 'Cobertura completa todo el año',
      features: [
        { text: '840 minutos de consultas en especialidades incluidas' },
        { text: 'Más minutos a precio especial' },
        { text: '60 preguntas de orientación médica' },
        { text: 'Recetas y planes personalizados' },
        { text: 'Acceso a todas las funciones de la App 24/7' },
        { text: 'Renovación automática, cancela cuando desees' },
        { text: 'Precios especiales en suplementos y productos de marketplace' }
      ],
      highlighted: true,
      badge: 'Más popular',
      cta: 'Elegir plan anual',
      isMembership: true
    },
    {
      name: 'Consulta Individual',
      price: '450',
      description: 'Consulta única cuando la necesites',
      priceNote: 'por consulta',
      features: [
        { text: 'Elige cualquier especialidad' },
        { text: 'Sin compromiso mensual' },
        { text: 'Ideal para casos específicos' },
        { text: 'Receta digital incluida' },
      ],
      highlighted: false,
      cta: 'Agendar consulta',
      isMembership: false
    }
  ];

  const handlePlanClick = (plan) => {
    if (plan.isMembership) {
      if (onSelectMembership) {
        onSelectMembership({
          name: plan.name,
          price: plan.price,
          membershipId: plan.membershipId,
          features: plan.features,
          highlighted: plan.highlighted
        });
      }
    } else {
      if (onStartBooking) {
        onStartBooking();
      }
    }
  };

  return (
    <section className="mb-24">
      <div className="text-center mb-12">
        <span className="inline-flex items-center gap-2 bg-brand-tint text-brand-deep font-semibold rounded-full px-3.5 py-1.5 text-[12px] mb-4">
          <Sparkles size={13} className="text-brand" aria-hidden="true" />
          Membresías
        </span>
        <h2 className="text-[clamp(1.7rem,4vw,3rem)] font-bold text-brand-deep tracking-[-0.025em] text-balance">
          Elige el plan perfecto <span className="text-brand">para ti.</span>
        </h2>
        <p className="text-[clamp(1rem,1.4vw,1.15rem)] text-text-body mt-4 max-w-xl mx-auto">
          Sin letras chicas, sin sorpresas. Cancela cuando quieras.
        </p>
      </div>

      <div className="grid md:grid-cols-3 gap-6 items-start">
        {plans.map((plan, index) => (
          <div
            key={index}
            className={`rounded-card p-7 transition-all ${
              plan.highlighted
                ? 'bg-brand text-white shadow-brand md:scale-105 md:z-10 relative'
                : 'bg-surface-card'
            }`}
          >
            {plan.badge && (
              <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                <div className="bg-amber-pill text-amber-deep px-3 py-1 rounded-full text-[11.5px] font-bold inline-flex items-center gap-1.5 shadow-soft">
                  <Star size={11} className="fill-amber-deep" aria-hidden="true" />
                  {plan.badge}
                </div>
              </div>
            )}

            <div className="mb-6">
              <h3 className={`text-[1.2rem] font-bold mb-2 tracking-[-0.015em] ${plan.highlighted ? 'text-white' : 'text-brand-deep'}`}>
                {plan.name}
              </h3>
              <p className={`text-sm ${plan.highlighted ? 'text-white/80' : 'text-text-body'}`}>
                {plan.description}
              </p>
            </div>

            <div className="mb-6">
              <div className="flex items-baseline gap-2">
                <span className={`text-[clamp(2.2rem,5vw,2.6rem)] font-bold tracking-[-0.03em] ${plan.highlighted ? 'text-white' : 'text-brand-deep'}`}>
                  ${plan.isMembership ? (backendPricing[plan.membershipId]?.monthlyPrice ?? plan.price) : plan.price}
                </span>
                <span className={`text-sm ${plan.highlighted ? 'text-white/75' : 'text-text-mute'}`}>
                  {plan.priceNote || 'MXN/mes'}
                </span>
              </div>
            </div>

            <ul className="space-y-3 mb-8">
              {plan.features.map((feature, idx) => (
                <li key={idx} className="flex items-start gap-3">
                  <Check
                    size={18}
                    className={`flex-shrink-0 mt-0.5 ${
                      plan.highlighted ? 'text-amber-pill' : 'text-brand'
                    }`}
                    aria-hidden="true"
                  />
                  <div className="flex-1">
                    <span className={`text-sm ${feature.bold ? 'font-bold' : ''} ${
                      plan.highlighted ? 'text-white' : 'text-text-strong'
                    }`}>
                      {feature.text}
                    </span>
                    {feature.duration && (
                      <span className={`text-xs ml-2 ${
                        plan.highlighted ? 'text-white/70' : 'text-text-mute'
                      }`}>
                        ({feature.duration})
                      </span>
                    )}
                    {feature.unlimited && (
                      <span className={`text-xs ml-2 font-semibold ${
                        plan.highlighted ? 'text-amber-pill' : 'text-brand'
                      }`}>
                        Ilimitadas
                      </span>
                    )}
                  </div>
                </li>
              ))}
            </ul>

            <button
              onClick={() => handlePlanClick(plan)}
              className={`press-scale w-full py-3.5 rounded-full font-semibold text-base transition-colors cursor-pointer ${
                plan.highlighted
                  ? 'bg-white text-brand-dark hover:bg-brand-tint'
                  : 'bg-brand text-white hover:bg-brand-dark'
              }`}
            >
              {plan.cta}
            </button>
          </div>
        ))}
      </div>

      <div className="mt-12 text-center">
        <p className="text-text-body mb-4 text-sm">
          ¿No estás segura? <span className="font-semibold text-brand">Haz un test gratis</span> y recibe recomendaciones personalizadas.
        </p>
      </div>
    </section>
  );
}
